import type { ListParams, PaginationInfo } from '@eo/shared';

import getApiHttpClient from '../../../httpClients/getApiHttpClient';
import { ANALYSE_POLL_MS, ANALYSE_TIMEOUT_MS } from '../constants';
import type {
  AnalysedDatesResponse,
  ClientOption,
  CreateEntryBody,
  CreateEntryResponse,
  DayTimelineResponse,
  DismissalBody,
  JobResponse,
  GoogleStartResponse,
  ParentTaskOption,
  SetTaskTypeBody,
  TaskTypeOption,
  UpdateEntryBody,
} from './types';

export async function fetchGoogleStartUrl(): Promise<GoogleStartResponse> {
  const res = await getApiHttpClient().get<GoogleStartResponse>(
    '/api/v1/cfo/integrations/google/start',
  );
  return res.data;
}

function asUser<T extends object>(params: T, userEmail: string | null): T {
  return userEmail ? { ...params, userEmail } : params;
}

export async function fetchDayTimeline(
  date: string,
  userEmail: string | null,
): Promise<DayTimelineResponse> {
  const res = await getApiHttpClient().get<DayTimelineResponse>('/api/v1/cfo/time/day', {
    params: asUser({ date }, userEmail),
  });
  return res.data;
}

// A POST: it costs dozens of upstream calls and stores what it finds.
//
// `refresh` re-reads the sources that cache instead of answering from what was
// stored. Asking again for a day already analysed can only mean the stored
// answer was not the wanted one, and Slack in particular is never re-read
// without it — a day whose synthesis failed the first time would otherwise show
// its raw sessions forever, and pressing the button would change nothing.
const TERMINAL: Array<JobResponse['status']> = ['succeeded', 'failed', 'partial'];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Nothing supervises the queue in the service, so the work runs inside a
// request — this one. It is deliberately not awaited: the drain outlives the
// browser's interest in it, and progress is read from the job instead.
function nudgeDrain(): void {
  void getApiHttpClient()
    .post('/api/v1/cfo/jobs/drain', {}, { timeout: ANALYSE_TIMEOUT_MS })
    .catch(() => undefined);
}

export async function fetchJob(id: string, userEmail: string | null): Promise<JobResponse> {
  const res = await getApiHttpClient().get<JobResponse>(
    `/api/v1/cfo/jobs/${id}`,
    userEmail ? { params: { userEmail } } : undefined,
  );
  return res.data;
}

// Pressing Analyse queues the derivation and answers at once; this is the wait
// that used to be one very long request. No single call here can time out, so
// a day that takes four minutes to read is no longer a day that fails.
export async function analyseDay(
  date: string,
  refresh: boolean,
  userEmail: string | null,
): Promise<DayTimelineResponse> {
  const queued = await getApiHttpClient().post<JobResponse>(
    '/api/v1/cfo/time/analysis',
    asUser({ date, refresh }, userEmail),
  );

  let job = queued.data;
  const deadline = Date.now() + ANALYSE_TIMEOUT_MS;
  while (!TERMINAL.includes(job.status)) {
    // Re-nudged while it is still queued: one drain runs one job, so a job
    // behind someone else's in the queue needs another request to pick it up.
    if (job.status === 'queued') nudgeDrain();
    if (Date.now() > deadline) throw new Error('This day is taking longer than expected.');
    await sleep(ANALYSE_POLL_MS);
    job = await fetchJob(job.id, userEmail);
  }

  if (job.status !== 'succeeded') {
    throw new Error(job.error ?? 'The day could not be read.');
  }
  return fetchDayTimeline(date, userEmail);
}

export async function fetchAnalysedDates(
  from: string,
  to: string,
  userEmail: string | null,
): Promise<AnalysedDatesResponse> {
  const res = await getApiHttpClient().get<AnalysedDatesResponse>('/api/v1/cfo/time/analysed', {
    params: asUser({ from, to }, userEmail),
  });
  return res.data;
}

export async function createEntry(body: CreateEntryBody): Promise<CreateEntryResponse> {
  const res = await getApiHttpClient().post<CreateEntryResponse>('/api/v1/cfo/time/entries', body);
  return res.data;
}

export async function updateEntry(entryId: string, body: UpdateEntryBody): Promise<void> {
  await getApiHttpClient().put(`/api/v1/cfo/time/entries/${entryId}`, body);
}

export async function setEntryTaskType(entryId: string, body: SetTaskTypeBody): Promise<void> {
  await getApiHttpClient().put(`/api/v1/cfo/time/entries/${entryId}/task-type`, body);
}

// One page at the route's ceiling: the registry is a short, admin-kept list.
export async function fetchTaskTypes(): Promise<{ taskTypes: TaskTypeOption[] }> {
  const res = await getApiHttpClient().get<{ taskTypes: TaskTypeOption[] }>(
    '/api/v1/cfo/task-types',
    { params: { perPage: 100 } },
  );
  return res.data;
}

export async function deleteEntry(entryId: string): Promise<void> {
  await getApiHttpClient().delete(`/api/v1/cfo/time/entries/${entryId}`);
}

export async function dismissSuggestion(body: DismissalBody): Promise<void> {
  await getApiHttpClient().post('/api/v1/cfo/time/dismissals', body);
}

// The route searches, filters and pages; the page size is its default.
export async function fetchClients({
  search,
  page,
}: ListParams): Promise<{ clients: ClientOption[]; pagination: PaginationInfo }> {
  const res = await getApiHttpClient().get<{
    clients: ClientOption[];
    pagination: PaginationInfo;
  }>('/api/v1/cfo/clients', { params: { active: true, ...(search && { search }), page } });
  return res.data;
}

export async function searchTaskTypes({
  search,
  page,
  filters,
}: ListParams & { filters?: Record<string, string | boolean> }): Promise<{
  taskTypes: TaskTypeOption[];
  pagination: PaginationInfo;
}> {
  const res = await getApiHttpClient().get<{
    taskTypes: TaskTypeOption[];
    pagination: PaginationInfo;
  }>('/api/v1/cfo/task-types', {
    params: {
      active: true,
      ...(search && { search }),
      ...(filters?.department && { department: filters.department }),
      page,
    },
  });
  return res.data;
}

export async function fetchClientTasks(
  clientId: string,
  { search, page }: ListParams,
): Promise<{ tasks: ParentTaskOption[]; pagination: PaginationInfo }> {
  const res = await getApiHttpClient().get<{
    tasks: ParentTaskOption[];
    pagination: PaginationInfo;
  }>(`/api/v1/cfo/clients/${clientId}/tasks`, { params: { ...(search && { search }), page } });
  return res.data;
}
